/**
 * The feed chrome drawn over each card: what makes a creative read as an ad
 * in someone's phone rather than a video on a wall.
 *
 * Avatar, handle and "Sponsored" at the top; the like, comment and share rail
 * down the right; two caption lines, a call-to-action pill and a progress bar
 * at the foot. No platform's own marks are drawn, and no platform is named:
 * the shapes are the ones every vertical feed shares, which is enough for
 * the eye to place the card and not enough to be anyone's trademark.
 *
 * Prerendered once per creative, as a transparent slot-sized layer. The video
 * path replaces a slot's pixels on every new frame, so the chrome has to be
 * stamped back each time; stamping one bitmap is cheap, laying out text is
 * not. The counts are decorative, as they were on the drawn cards: a wall of
 * ads with empty rails looks unposted.
 */

import { cardRect, SLOT_H, SLOT_W } from "./adWallAtlas";

const CTAS = [
  "Shop now",
  "Learn more",
  "Download",
  "Get offer",
  "Try it free",
  "Order now",
];

const COUNTS = ["12.4K", "3.1K", "88K", "540", "1.2M", "27K", "9.8K"];

/** Avatar fills. Palette colours on purpose: an avatar is a picture, not a role. */
const ACCENTS = [
  "#ff6a3d",
  "#12a37f",
  "#e09a00",
  "#3d5cff",
  "#ff3d9a",
  "#6fb02a",
  "#ffb13d",
  "#c96a2c",
  "#7a4dff",
  "#0aa3cc",
];

type Ctx = CanvasRenderingContext2D;

function rr(c: Ctx, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

function heart(c: Ctx, cx: number, cy: number, s: number) {
  c.beginPath();
  c.moveTo(cx, cy + s * 0.9);
  c.bezierCurveTo(
    cx - s * 1.6,
    cy - s * 0.2,
    cx - s * 0.9,
    cy - s * 1.3,
    cx,
    cy - s * 0.5,
  );
  c.bezierCurveTo(
    cx + s * 0.9,
    cy - s * 1.3,
    cx + s * 1.6,
    cy - s * 0.2,
    cx,
    cy + s * 0.9,
  );
  c.closePath();
  c.fill();
}

function bubble(c: Ctx, cx: number, cy: number, s: number) {
  rr(c, cx - s, cy - s * 0.8, s * 2, s * 1.5, s * 0.6);
  c.fill();
  c.beginPath();
  c.moveTo(cx - s * 0.5, cy + s * 0.6);
  c.lineTo(cx - s * 0.7, cy + s * 1.1);
  c.lineTo(cx, cy + s * 0.7);
  c.closePath();
  c.fill();
}

function share(c: Ctx, cx: number, cy: number, s: number) {
  c.beginPath();
  c.moveTo(cx + s * 1.1, cy - s * 0.2);
  c.lineTo(cx + s * 0.1, cy - s * 1.0);
  c.lineTo(cx + s * 0.1, cy - s * 0.5);
  c.bezierCurveTo(
    cx - s * 0.9,
    cy - s * 0.5,
    cx - s * 1.1,
    cy + s * 0.4,
    cx - s * 1.1,
    cy + s * 1.0,
  );
  c.bezierCurveTo(
    cx - s * 0.7,
    cy + s * 0.3,
    cx - s * 0.3,
    cy + s * 0.1,
    cx + s * 0.1,
    cy + s * 0.1,
  );
  c.lineTo(cx + s * 0.1, cy + s * 0.6);
  c.closePath();
  c.fill();
}

const GLYPHS = [heart, bubble, share];

/**
 * Deterministic, so a card's progress bar sits in the same place on every
 * load rather than jumping between visits.
 */
function progressFor(index: number) {
  return 0.15 + ((index * 37) % 70) / 100;
}

/**
 * One creative's chrome, as a transparent layer the size of a slot.
 *
 * `family` is the resolved sans stack, as for the badge: canvas cannot see a
 * CSS variable. `brand` becomes the handle, lower-cased and run together.
 */
export function buildChrome(
  index: number,
  brand: string,
  family: string,
): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = SLOT_W;
  cv.height = SLOT_H;
  const c = cv.getContext("2d");
  if (!c) return cv;
  const { x, y, w, h } = cardRect(0, 0);

  let shade = c.createLinearGradient(x, y, x, y + h * 0.3);
  shade.addColorStop(0, "rgba(0,0,0,0.5)");
  shade.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = shade;
  c.fillRect(x, y, w, h * 0.3);
  shade = c.createLinearGradient(x, y + h * 0.55, x, y + h);
  shade.addColorStop(0, "rgba(0,0,0,0)");
  shade.addColorStop(1, "rgba(0,0,0,0.65)");
  c.fillStyle = shade;
  c.fillRect(x, y + h * 0.55, w, h * 0.45);

  c.fillStyle = "#fff";
  c.beginPath();
  c.arc(x + 32, y + 34, 16, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = ACCENTS[index % ACCENTS.length];
  c.beginPath();
  c.arc(x + 32, y + 34, 13, 0, Math.PI * 2);
  c.fill();
  c.textAlign = "left";
  c.textBaseline = "alphabetic";
  c.fillStyle = "#fff";
  c.font = `600 17px ${family}`;
  c.fillText(`@${brand.toLowerCase().replace(/\s+/g, "")}`, x + 56, y + 31);
  c.fillStyle = "rgba(255,255,255,0.75)";
  c.font = `400 13px ${family}`;
  c.fillText("Sponsored", x + 56, y + 49);

  // The rail sits above the lower caption area so the badge, which rises
  // through the right-hand side of the card, has somewhere to go.
  GLYPHS.forEach((glyph, k) => {
    const cy = y + h * 0.5 + k * 64;
    const cx = x + w - 34;
    c.fillStyle = "rgba(255,255,255,0.22)";
    c.beginPath();
    c.arc(cx, cy, 19, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "rgba(255,255,255,0.95)";
    glyph(c, cx, cy, 8);
    c.font = `600 12px ${family}`;
    c.textAlign = "center";
    c.fillStyle = "#fff";
    c.fillText(COUNTS[(index + k) % COUNTS.length], cx, cy + 36);
  });

  c.fillStyle = "rgba(255,255,255,0.85)";
  rr(c, x + 20, y + h - 146, w * 0.56, 9, 4.5);
  c.fill();
  c.fillStyle = "rgba(255,255,255,0.55)";
  rr(c, x + 20, y + h - 128, w * 0.38, 9, 4.5);
  c.fill();

  c.fillStyle = "#fff";
  rr(c, x + 18, y + h - 100, w - 36, 48, 14);
  c.fill();
  c.fillStyle = "#141414";
  c.font = `700 18px ${family}`;
  c.textAlign = "center";
  c.fillText(CTAS[index % CTAS.length], x + w / 2, y + h - 69);

  c.fillStyle = "rgba(255,255,255,0.3)";
  c.fillRect(x + 18, y + h - 30, w - 36, 4);
  c.fillStyle = "#fff";
  c.fillRect(x + 18, y + h - 30, (w - 36) * progressFor(index), 4);

  return cv;
}
